import { v4 as uuid } from 'uuid';

import { deepClone } from './deepClone';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type { ExtraElementData, Video } from '@src/types.ts/story';

// Define an interface for the element data with layout properties
interface ElementExtraData {
  layoutPosition?: 'hidden' | 'top' | 'bottom';
  layoutAssociatedIds?: string[];
  layoutType?: 'stacked-1-1' | 'stacked-1-3';
  [key: string]: any;
}

// Define the result type for the findLayoutRelatedElements function
export interface LayoutElementsResult {
  hiddenParentId: string | null;
  layoutChildrenIds: string[];
  isHiddenParent: boolean;
  isLayoutChild: boolean;
  layoutType?: 'stacked-1-1' | 'stacked-1-3';
  layoutPosition?: 'hidden' | 'top' | 'bottom';
}

/**
 * Finds layout-related elements based on an element ID
 *
 * @param store VideoCreatorStore instance, or undefined if extraElementDataOverride is provided
 * @param elementId ID of element to check
 * @param extraElementDataOverride Extra element data to use instead of store's extraElementData
 * @returns Object containing hidden parent element ID and child layout element IDs
 */
export function findLayoutRelatedElements(
  store: NonNullable<typeof extraElementDataOverride> extends never
    ? IVideoCreatorStore
    : IVideoCreatorStore | undefined,
  elementId: string,
  extraElementDataOverride?: Video['extraElementData'],
): LayoutElementsResult {
  const result: LayoutElementsResult = {
    hiddenParentId: null,
    layoutChildrenIds: [],
    isHiddenParent: false,
    isLayoutChild: false,
  };

  // Check if this element is a layout element
  const extraElementData = (extraElementDataOverride ||
    store?.currentVideo?.extraElementData ||
    {}) as Record<string, ElementExtraData>;
  const elementData: ElementExtraData | undefined = extraElementData[elementId];

  if (!elementData) return result;

  // Case 1: This is a hidden parent element
  if (
    elementData.layoutPosition === 'hidden' &&
    elementData.layoutAssociatedIds &&
    elementData.layoutAssociatedIds.length > 0
  ) {
    result.isHiddenParent = true;
    result.hiddenParentId = elementId;
    result.layoutChildrenIds = elementData.layoutAssociatedIds;
    result.layoutType = elementData.layoutType;
    result.layoutPosition = elementData?.layoutPosition;
    return result;
  }

  // Case 2: This is a layout child (composition element)
  if (
    (elementData.layoutPosition === 'top' ||
      elementData.layoutPosition === 'bottom') &&
    elementData.layoutAssociatedIds &&
    elementData.layoutAssociatedIds.length > 0
  ) {
    result.isLayoutChild = true;
    result.hiddenParentId = elementData.layoutAssociatedIds[0];
    result.layoutType = elementData.layoutType;
    result.layoutPosition = elementData.layoutPosition;

    // Find all children of the same hidden parent
    for (const [id, data] of Object.entries(extraElementData)) {
      if (
        id !== elementId &&
        (data.layoutPosition === 'top' || data.layoutPosition === 'bottom') &&
        data.layoutAssociatedIds &&
        data.layoutAssociatedIds.length > 0 &&
        data.layoutAssociatedIds[0] === result.hiddenParentId
      ) {
        result.layoutChildrenIds.push(id);
      }
    }

    result.layoutChildrenIds.push(elementId);
    return result;
  }

  return result;
}

/**
 * Creates modifications to synchronize properties between hidden parent element and its layout children
 *
 * @param store VideoCreatorStore instance
 * @param modifications Existing modifications to enhance
 * @returns Enhanced modifications that include sync operations for layout elements
 */
export function syncLayoutElementModifications(
  store: VideoCreatorStore,
  modifications: Record<string, any>,
): Record<string, any> {
  if (!modifications || typeof modifications !== 'object') return modifications;

  // Clone the modifications to avoid mutating the original
  const syncedModifications = { ...modifications };

  // Properties that should be synchronized between hidden parent and layout children
  const syncProperties = [
    'time',
    'duration',
    'trim_start',
    'trim_end',
    'start',
    'end',
    'text',
  ];

  // Track which elements we've already processed to avoid infinite loops
  const processedElements: string[] = [];

  // Process each modification
  for (const key of Object.keys(modifications)) {
    // Parse the key to get elementId and property
    const [elementId, property] = key.split('.');
    if (!elementId || !property || processedElements.includes(elementId))
      continue;

    // Skip non-sync properties
    if (!syncProperties.includes(property)) continue;

    // Find related layout elements
    const layoutElements = findLayoutRelatedElements(store, elementId);

    // Add element to processed array
    processedElements.push(elementId);

    // Case 1: This is a hidden parent element - sync to children
    if (
      layoutElements.isHiddenParent &&
      layoutElements.layoutChildrenIds.length > 0
    ) {
      for (const childId of layoutElements.layoutChildrenIds) {
        if (!processedElements.includes(childId)) {
          syncedModifications[`${childId}.${property}`] = modifications[key];
          processedElements.push(childId);
        }
      }
    }

    // Case 2: This is a layout child - sync to parent and siblings
    else if (layoutElements.isLayoutChild && layoutElements.hiddenParentId) {
      // Sync to hidden parent
      if (!processedElements.includes(layoutElements.hiddenParentId!)) {
        syncedModifications[`${layoutElements.hiddenParentId}.${property}`] =
          modifications[key];
        processedElements.push(layoutElements.hiddenParentId!);
      }

      // Sync to sibling layout elements
      for (const siblingId of layoutElements.layoutChildrenIds) {
        if (siblingId !== elementId && !processedElements.includes(siblingId)) {
          syncedModifications[`${siblingId}.${property}`] = modifications[key];
          processedElements.push(siblingId);
        }
      }
    }
  }

  return syncedModifications;
}

// trim doesn't work on composition
export function pickUnderlyingVideoElement(
  elementSource: Record<string, any>,
): Record<string, any> | undefined {
  if (elementSource.type === 'composition') {
    const innerEl = elementSource.elements.find(
      (el: any) => el.type === 'video',
    );
    return innerEl;
  } else {
    return elementSource;
  }
}

export function getAllLayoutChildrenIds(
  extraElementData: Video['extraElementData'],
): string[] {
  return getAllLayoutElementsWithPosition(
    extraElementData,
    p => p === 'top' || p === 'bottom',
  );
}

export function getAllLayoutParentIds(
  extraElementData: Video['extraElementData'],
) {
  return getAllLayoutElementsWithPosition(
    extraElementData,
    p => p === 'hidden',
  );
}

/**
 * Computes a percentage scale string to zoom a 16:9 source inside a 9:16 container fragment
 * used by stacked layouts. The scale depends on the composition height fraction (h),
 * ensuring each split can be aligned left/right without showing the other split (no cutouts).
 *
 * Returns a string with '%' suffix (e.g., '126.563%').
 */
export function computeStackedLayoutScaleForHeight(
  heightPercent: string,
): string {
  // Reference visible fraction at h=0.5 (1:1 split): 81/128 = 0.6328125
  // Used as a multiplier to reach at least half-width coverage when aligning left/right.

  /*
    Vlad's explanation:
    overfill(h) = r_src / r_comp = (16/9) / (9/(16·h)) = (256 · h) / 81visibleFraction(0.5) = 81 / (256 · 0.5) = 81 / 128 
    = 0.6328125

    Why are we using visibleFraction(0.5) as a constant for both 1:1 and 1:3?
    Short answer: It’s a heuristic reference constant, not a physical requirement.
    We chose the 1:1 case’s visible fraction (≈0.6328) and generalized by dividing it by h (i.e., scale ∝ 1/h) to keep the top (shorter) pane of a 1:3 layout more aggressively zoomed so its apparent horizontal crop doesn’t feel too narrow compared to the 1:1 experience, while letting taller panes stay near or at 100%.
    h = 0.50 (1:1) → scale ≈ 126.563%
    h = 0.333 (top of 1:3) → scale ≈ 189.844%
    h = 0.667 (bottom of 1:3) → raw ≈ 94.9% → clamped to 100%
  */
  const REF_VISIBLE_FRACTION = 81 / 128;
  const h = parseFloat(heightPercent) / 100; // fraction of full stage height
  if (isNaN(h) || h <= 0) return '100%';
  const scale = Math.max(1, REF_VISIBLE_FRACTION / h) * 100;
  return `${scale.toFixed(3)}%`;
}

function getAllLayoutElementsWithPosition(
  extraElementData: Video['extraElementData'],
  positionCompareFn: (p: string | undefined) => boolean,
) {
  const result = [];
  for (const [id, rawData] of Object.entries(extraElementData)) {
    const { layoutPosition } = rawData as ExtraElementData;
    if (positionCompareFn(layoutPosition)) {
      result.push(id);
    }
  }
  return result;
}

export function adjustRelevantStackedLayoutChildrenTo(
  newTracks: Record<string, any>[],
  elements: Record<string, any>[],
  extraElementData: Video['extraElementData'],
): Record<string, any>[] {
  // result will contain updated layout children that are still relevant to newTrack
  const result = [];
  // First identify all layout elements and their relationships
  const layoutElements = new Map<string, ExtraElementData>();
  // Store all layout-related elements
  for (const [id, rawData] of Object.entries(extraElementData)) {
    // Type cast to ExtraElementData to access layout properties
    const data = rawData as ExtraElementData;

    // Only store elements that have layout properties
    if (
      data.layoutPosition &&
      data.layoutAssociatedIds &&
      data.layoutAssociatedIds.length > 0
    ) {
      layoutElements.set(id, data);
    }
  }
  // Now check if any elements we're modifying are layout-related
  for (const element of elements) {
    const id = element.id;

    // Check if this is a layout element
    const layoutData = layoutElements.get(id);
    if (!layoutData) continue;

    // Case 1: This is a hidden parent element,
    // we don't need to handle child cases, because parent will be getting updates,
    // and we will be propagating them to children, not the other way around
    if (
      layoutData.layoutPosition === 'hidden' &&
      layoutData.layoutAssociatedIds
    ) {
      // Search for this element in newTracks
      const updatedElement = newTracks.find(track => track.id === id);
      if (!updatedElement) continue;

      // For each child element (which are the associated composition elements)
      for (const childId of layoutData.layoutAssociatedIds) {
        const childSource = elements.find(element => element.id === childId);
        if (!childSource) continue;
        result.push(syncLayoutChildToParent(childSource, updatedElement));
      }
    }
  }
  return result;
}

function syncLayoutChildToParent(
  childSourceInput: Record<string, any>,
  updatedElement: Record<string, any>,
): Record<string, any> {
  const childSource = deepClone(childSourceInput);
  // Sync time-related properties
  childSource.time = updatedElement.time;
  childSource.duration = updatedElement.duration;
  if (updatedElement.trim_start != null) {
    const childSourceForTrim = pickUnderlyingVideoElement(childSource);
    if (childSourceForTrim) {
      childSourceForTrim.trim_start = updatedElement.trim_start;
    }
  }
  if (updatedElement.trim_end != null) {
    const childSourceForTrim = pickUnderlyingVideoElement(childSource);
    if (childSourceForTrim) {
      childSourceForTrim.trim_end = updatedElement.trim_end;
    }
  }
  return childSource;
}

export function attachExistingLayoutChildrenToNewParent(
  splitElementPairs: { originalId: string; newId: string }[],
  newTracks: Record<string, any>[],
  elements: Record<string, any>[],
  store: VideoCreatorStore,
): Record<string, any>[] {
  const result = [];
  for (const { originalId, newId } of splitElementPairs) {
    const layoutElements = findLayoutRelatedElements(store, originalId);

    if (!layoutElements.isHiddenParent) {
      continue;
    }

    const updatedElement = newTracks.find(track => track.id === newId);
    if (!updatedElement) continue;

    const targetChildren = layoutElements.layoutChildrenIds
      .map(id => {
        return elements.find((el: any) => el.id === id);
      })
      .filter((el): el is Record<string, any> => el !== undefined);

    if (targetChildren.length !== 2) {
      throw new Error(
        'Expected 2 children, but found ' + targetChildren.length,
      );
    }

    const extraElementData = (store.currentVideo?.extraElementData ||
      {}) as Record<string, ElementExtraData>;

    const destChildren = targetChildren.map(element => {
      const updatedChild = syncLayoutChildToParent(element, updatedElement);

      const childEed = extraElementData[updatedChild.id];
      if (!childEed) {
        throw new Error('Child element not found in extraElementData');
      }

      const newChild = { ...updatedChild };
      newChild.id = uuid();
      const innerVideoEl = pickUnderlyingVideoElement(newChild);
      if (innerVideoEl) {
        innerVideoEl.id = uuid();
      }

      extraElementData[newChild.id] = {
        ...childEed,
        layoutAssociatedIds: [newId],
      };

      return newChild;
    });

    extraElementData[newId] = {
      ...extraElementData[originalId],
      layoutAssociatedIds: destChildren.map(el => el.id),
    };

    result.push(...destChildren);
  }

  return result;
}

export function moveUpToHiddenParent(
  elementId: string,
  extraElementData: Video['extraElementData'],
): string {
  let eed = extraElementData[elementId] as ExtraElementData | null;
  if (
    eed &&
    eed.layoutPosition &&
    eed.layoutPosition !== 'hidden' &&
    eed.layoutAssociatedIds &&
    eed.layoutAssociatedIds.length > 0
  ) {
    elementId = eed.layoutAssociatedIds[0];
  }
  return elementId;
}
